import { Space, Tag } from "antd";
import { formatIsoDate } from "../../../utils/generalUtils";
import { classifyLoad } from "../../../utils/fsrUtils";
import { renderFsrStatus } from "../../../utils/fsrApprovalUtils";
import ViewingFormButton from "../../Form/ViewingFormButton";
import CancelSubmissionButton from "../CancelSubmissionButton";

export const fsrConfig = {
  docType: "Faculty Service Record",
  title: "Faculty Service Record",
  requireFaculty: true,
  description:
    "Submit your Faculty Service Record for approval. Your submission will be reviewed by your selected program chairs and dean.",
  viewingType: "pdf",
  viewingPdfEndpoint: "/fsr/pdf/snapshot",
  viewingModalWidth: 1000,
  reviewPdfEndpoint: "/fsr/pdf/live",
  apiConfig: {
    get: "/employee/fsr",
    post: "/employee/addfsr",
    cancel: "/employee/cancel/fsr",
  },
  dataMapper: (item, index) => ({
    ...item,
    key: index + 1,

    status: item?.submission?.derived_status || item?.submission?.status,
    remarks: item?.submission?.remarks,
    created_at: item?.created_at,
    submission_id: item?.submission?.id,
    all_approvals: item?.submission?.approval || [],
  }),
  columns: (handleRefresh) => [
    { title: "No.", dataIndex: "key", key: "key", width: 60 },
    {
      title: "Academic Term",
      dataIndex: "academic_term",
      key: "academic_term",
    },
    {
      title: "Units",
      key: "units",
      render: (_, record) => {
        const totalLoad = record?.totals?.total_faculty_load;
        if (totalLoad === undefined || totalLoad === null) return "—";

        const { status, units } = classifyLoad(totalLoad, record?.academic_term);
        const displayLoad = parseFloat(Number(totalLoad).toFixed(2));

        if (status === "Normal") {
          return `${displayLoad} (Normal Load)`;
        }

        const displayUnits = parseFloat(Number(units).toFixed(2));
        return `${displayLoad} (${displayUnits} Units ${status}ed)`;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        if (status === "cancelled") {
          return <Tag color="purple">CANCELLED</Tag>;
        }

        return renderFsrStatus(status, record);
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (value) => (value ? value : "—"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        // Program Chairs are the first line of approval — once any of them
        // has decided, the submission can no longer be cancelled, even
        // though the Dean hasn't acted yet and the submission is still
        // technically "pending" overall.
        const anyChairDecided = (item?.all_approvals || []).some(
          (approval) =>
            approval.status === "approved" || approval.status === "disapproved"
        );
        const notPending = item?.submission?.status !== "pending";
        const cannotCancel = notPending || anyChairDecided;

        return (
          <Space>
            <ViewingFormButton
              contents={item}
              viewingType={fsrConfig.viewingType}
              viewingPdfEndpoint={fsrConfig.viewingPdfEndpoint}
              viewingModalWidth={fsrConfig.viewingModalWidth}
              isIcon={true}
            />
            <CancelSubmissionButton
              cancelUrl={`${fsrConfig.apiConfig.cancel}/${item?.id}`}
              disabled={cannotCancel}
              disabledReason={
                notPending
                  ? "This submission is no longer pending."
                  : "A program chair has already made a decision — this submission can no longer be cancelled."
              }
              onCancelled={handleRefresh}
            />
          </Space>
        );
      },
    },
    {
      title: "Date Submitted",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => formatIsoDate(value),
    },
  ],
  formFields: [
    {
      name: "academic_term",
      label: "Academic Term",
      type: "term-select",
      rules: [{ required: true, message: "This is required" }],
    },
    {
      name: "status_of_appointment",
      label: "Status of Appointment",
      type: "appointment-status-select",
      rules: [{ required: true, message: "This is required" }],
    },
    {
      name: "program_chairs",
      label: "Program Chairs",
      type: "chair-select",
      rules: [{ required: true, message: "This is required" }],
    },
    {
      name: "dean",
      label: "Dean",
      type: "dean-select",
      rules: [{ required: true, message: "This is required" }],
    },
  ],
};
